import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Send,
  CheckCircle2,
  XCircle,
  User,
  Calendar,
  FileText,
  Code2,
  AlertTriangle,
  Loader2,
  Activity,
  Stethoscope,
  ClipboardList
} from 'lucide-react';
import {
  ClinicalSummaryData,
  CLINICAL_SECTION_LABELS,
  CLINICAL_SECTION_ORDER
} from '@/types/clinicalSummary';
import { apiService } from '@/services/api';
import type { DiagnosticNote, EMCode, ProcedureCptSuggestion } from '@/types/codingAssistant';
import { useFeatureFlag, useOrganizationFeatureFlags } from '@/stores/organizationFeatureFlagsStore';

interface EhrDeliveryConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirming: boolean;
  patientName: string;
  visitType: string;
  clinicalSummary: ClinicalSummaryData | null;
  approvedCodes: any[];
  noteStatus: string | null;
  resourceId: string;
  isEncounterMode: boolean;
}

export const EhrDeliveryConfirmModal: React.FC<EhrDeliveryConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  confirming,
  patientName,
  visitType,
  clinicalSummary,
  approvedCodes,
  noteStatus,
  resourceId,
  isEncounterMode
}) => {
  const emCodingAssistantEnabled = useFeatureFlag('emCodingAssistantEnabled');
  const cptCodingAssistantEnabled = useFeatureFlag('cptCodingAssistantEnabled');
  // True once real flags are fetched; before that the flags above read
  // fail-open defaults (true). Used to defer flag-gated fetches.
  const flagsLoaded = useOrganizationFeatureFlags((s) => s.isLoaded);
  // Clinical sections
  const sections = clinicalSummary?.aiGenerated;
  const capturedCount = sections
    ? CLINICAL_SECTION_ORDER.filter(key => sections[key]?.status === 'captured').length
    : 0;
  const totalSections = CLINICAL_SECTION_ORDER.length;

  // Coding assistant data (fetched when modal opens) — declare before use in displayApprovedCodes
  const [diagnosticNotes, setDiagnosticNotes] = useState<DiagnosticNote[]>([]);
  const [emCode, setEmCode] = useState<EMCode | null>(null);
  const [procedureCpts, setProcedureCpts] = useState<ProcedureCptSuggestion[]>([]);
  const [fetchedApprovedCodes, setFetchedApprovedCodes] = useState<any[] | null>(null);
  const [codingDataLoading, setCodingDataLoading] = useState(false);

  // Hide diagnostic-note rows that carry no approved codes and no clinician
  // text. Such rows can exist as a side-effect of lazy-create on first ICD-10
  // search (see DiagnosticNotesSection.handleSearchCodes) and would otherwise
  // render here as an "Empty note" card, misrepresenting the visit.
  const meaningfulDiagnosticNotes = diagnosticNotes.filter(n =>
    (n.suggestedCodes?.some(c => c.status === 'approved')) ||
    (typeof n.plainText === 'string' && n.plainText.trim().length > 0)
  );

  // Normalize code field names across encounter/appointment modes
  const isIcd = (c: any) => c.codeType === 'icd10' || c.system === 'ICD-10' || c.category === 'diagnosis';
  const isCpt = (c: any) => c.codeType === 'cpt' || c.system === 'CPT' || c.category === 'procedure';
  // Use freshly fetched codes when available so preview reflects current state after add/remove
  const displayApprovedCodes = fetchedApprovedCodes !== null ? fetchedApprovedCodes : approvedCodes;
  const icdCodes = displayApprovedCodes.filter(isIcd);
  // CPT codes are only surfaced in the Approved Codes Summary when the org
  // has the CPT coding assistant enabled. Even if a code was approved earlier
  // (flag was on), once the flag is off the summary hides it and backend
  // delivery strips it (see clinicalResultsDelivery.service.ts).
  const cptCodes = cptCodingAssistantEnabled ? displayApprovedCodes.filter(isCpt) : [];
  // hasNoCodes considers the *visible* set so the empty-state message is honest
  // about what will actually be sent.
  const hasNoCodes = icdCodes.length === 0 && cptCodes.length === 0;

  // Reset all coding state on close so reopening for a different encounter
  // never shows the prior patient's codes.
  useEffect(() => {
    if (!open) {
      setDiagnosticNotes([]);
      setEmCode(null);
      setProcedureCpts([]);
      setFetchedApprovedCodes(null);
    }
  }, [open]);

  // Fetch coding assistant data when modal opens. EM/CPT endpoints are
  // flag-gated server-side (403 when disabled), so only fetch them when the
  // flag is on. Diagnostic notes and approved codes are always available.
  useEffect(() => {
    if (!open || !resourceId) return;

    // Modal stays mounted across encounters; abort in-flight requests and
    // skip setState on cleanup so a stale fetch can't overwrite a newer one.
    let cancelled = false;
    const controller = new AbortController();
    const cfg = { signal: controller.signal };

    const fetchCodingData = async () => {
      setCodingDataLoading(true);
      const base = isEncounterMode
        ? `/v1/clinical/encounter/${resourceId}`
        : `/v1/clinical/${resourceId}`;

      try {
        const [diagRes, emRes, procRes, codesRes] = await Promise.all([
          apiService.get<{ success: boolean; data: { diagnosticNotes: DiagnosticNote[] } }>(
            `${base}/coding-assistant/diagnostic-notes`, undefined, cfg
          ).catch(() => null),
          // Fetch flag-gated endpoints only once flags are loaded and enabled;
          // otherwise they 403 for disabled orgs and trigger a spurious toast.
          flagsLoaded && emCodingAssistantEnabled
            ? apiService.get<{ success: boolean; data: { emCode: EMCode | null } }>(
                `${base}/em-code`, undefined, cfg
              ).catch(() => null)
            : Promise.resolve(null),
          flagsLoaded && cptCodingAssistantEnabled
            ? apiService.get<{ success: boolean; data: { suggestions: ProcedureCptSuggestion[] } }>(
                `${base}/procedure-cpt`, undefined, cfg
              ).catch(() => null)
            : Promise.resolve(null),
          apiService.get<{ success: boolean; data: { codes: any[] } }>(`${base}/codes`, undefined, cfg).catch(() => null),
        ]);
        if (cancelled) return;
        setDiagnosticNotes(diagRes?.data?.diagnosticNotes || []);
        setEmCode(emRes?.data?.emCode || null);
        setProcedureCpts(procRes?.data?.suggestions || []);
        setFetchedApprovedCodes(codesRes?.data?.codes ?? []);
      } catch {
        // Non-blocking — modal still works without coding data
      } finally {
        if (!cancelled) setCodingDataLoading(false);
      }
    };

    fetchCodingData();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, resourceId, isEncounterMode, emCodingAssistantEnabled, cptCodingAssistantEnabled, flagsLoaded]);

  // Derived: approved procedure CPTs only
  const approvedProcedures = procedureCpts.filter(p => p.status === 'approved');

  // Loading skeleton for coding sections
  const CodingLoadingSkeleton = () => (
    <div className="flex items-center gap-2 px-3 py-3 bg-gray-50 rounded-xl">
      <Loader2 className="text-gray-400 animate-spin flex-shrink-0" size={14} />
      <span className="text-sm text-gray-500">Loading...</span>
    </div>
  );

  // Amber empty-state warning
  const EmptyWarning = ({ message }: { message: string }) => (
    <div className="flex items-start gap-2 px-3 py-3 bg-amber-50 rounded-xl border border-amber-100">
      <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={14} />
      <span className="text-sm text-amber-700">{message}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-lg lg:max-w-xl p-0 gap-0 overflow-hidden rounded-2xl border-0 shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700 px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-semibold flex items-center gap-2">
              <Send size={18} />
              Send Clinical Results to EHR
            </DialogTitle>
            <DialogDescription className="text-purple-200 text-sm mt-1">
              Review the summary below before sending
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable body */}
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Patient & Visit */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-xl border border-blue-100">
              <User className="text-blue-600 flex-shrink-0" size={14} />
              <span className="text-sm font-medium text-gray-800">{patientName || 'Unknown Patient'}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-xl border border-purple-100">
              <Calendar className="text-purple-600 flex-shrink-0" size={14} />
              <span className="text-sm font-medium text-gray-800">{visitType || 'Visit'}</span>
            </div>
          </div>

          {/* Clinical Sections */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="text-gray-500" size={15} />
                <h3 className="text-sm font-semibold text-gray-900">Clinical Sections</h3>
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                capturedCount === totalSections
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {capturedCount}/{totalSections} captured
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {CLINICAL_SECTION_ORDER.map((key) => {
                const section = sections?.[key];
                const isCaptured = section?.status === 'captured';
                const label = CLINICAL_SECTION_LABELS[key];

                return (
                  <div
                    key={key}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      isCaptured
                        ? 'bg-green-50/60 text-gray-700'
                        : 'bg-gray-50 text-gray-400'
                    }`}
                  >
                    {isCaptured ? (
                      <CheckCircle2 className="text-green-500 flex-shrink-0" size={14} />
                    ) : (
                      <XCircle className="text-gray-300 flex-shrink-0" size={14} />
                    )}
                    <span className={isCaptured ? 'font-medium' : ''}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Diagnostic Codes */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="text-gray-500" size={15} />
              <h3 className="text-sm font-semibold text-gray-900">Diagnostic Codes</h3>
            </div>

            {codingDataLoading ? (
              <CodingLoadingSkeleton />
            ) : meaningfulDiagnosticNotes.length === 0 ? (
              <EmptyWarning message="No diagnostic codes approved. Diagnoses have not been coded yet." />
            ) : (
              <div className="space-y-2">
                {meaningfulDiagnosticNotes.map((note, i) => {
                  const noteApprovedCodes = note.suggestedCodes?.filter(c => c.status === 'approved') || [];
                  return (
                    <div key={note.id || i} className="p-3 bg-blue-50/50 rounded-lg border border-blue-100/80">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-blue-600">Diagnosis {i + 1}</span>
                        {noteApprovedCodes.length > 0 && (
                          <span className="text-xs text-gray-500">
                            {noteApprovedCodes.length} code{noteApprovedCodes.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {note.plainText || 'Empty note'}
                      </p>
                      {noteApprovedCodes.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {noteApprovedCodes.map((code, idx) => (
                            <div key={idx} className="flex items-baseline gap-2 px-2 py-1 bg-white/70 rounded text-xs">
                              <span className="font-mono font-bold text-blue-700 flex-shrink-0">{code.code}</span>
                              <span className="text-gray-600 truncate">{code.description}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* E&M Visit Code — gated by the per-org EM Coding Assistant flag. */}
          {emCodingAssistantEnabled && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="text-gray-500" size={15} />
                <h3 className="text-sm font-semibold text-gray-900">E&M Visit Code</h3>
              </div>

              {codingDataLoading ? (
                <CodingLoadingSkeleton />
              ) : !emCode ? (
                <EmptyWarning message="E&M code not generated. Visit complexity level has not been determined." />
              ) : (
                <div className="p-3 bg-purple-50/50 rounded-lg border border-purple-100/80">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-bold text-purple-700">{emCode.code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        emCode.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : emCode.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {emCode.status === 'approved' ? 'Approved' : emCode.status === 'rejected' ? 'Rejected' : 'Pending'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{emCode.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>
                      <span className="font-medium text-gray-600">MDM:</span>{' '}
                      <span className="capitalize">{emCode.mdmLevel}</span>
                    </span>
                    <span>
                      <span className="font-medium text-gray-600">Patient:</span>{' '}
                      <span className="capitalize">{emCode.patientType}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Procedure CPT Codes — gated by the per-org CPT Coding Assistant flag. */}
          {cptCodingAssistantEnabled && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Stethoscope className="text-gray-500" size={15} />
                <h3 className="text-sm font-semibold text-gray-900">Procedure CPT Codes</h3>
              </div>

              {codingDataLoading ? (
                <CodingLoadingSkeleton />
              ) : approvedProcedures.length === 0 ? (
                <EmptyWarning message="No procedure codes approved. Procedural CPT codes have not been extracted or approved." />
              ) : (
                <div className="space-y-2">
                  {approvedProcedures.map((proc, i) => (
                    <div key={i} className="p-3 bg-indigo-50/50 rounded-lg border border-indigo-100/80">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-sm font-mono font-bold text-indigo-700">{proc.cptCode}</span>
                        <span className="text-sm text-gray-700">{proc.description}</span>
                      </div>
                      {proc.evidenceText && (
                        <p className="text-xs text-gray-500 italic line-clamp-2 mt-1.5">
                          &ldquo;{proc.evidenceText}&rdquo;
                        </p>
                      )}
                      {proc.sourceSection && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-400">
                          <span>Source: {proc.source === 'transcript' ? 'Transcript' : 'Physician Note'}</span>
                          <span>&middot;</span>
                          <span>{proc.sourceSection}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Approved Codes Summary */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Code2 className="text-gray-500" size={15} />
              <h3 className="text-sm font-semibold text-gray-900">Approved Codes Summary</h3>
            </div>

            {hasNoCodes ? (
              <EmptyWarning message="No codes approved yet. Results will be sent without diagnostic codes." />
            ) : (
              <div className="space-y-2">
                {icdCodes.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">ICD-10 Diagnoses</span>
                    {icdCodes.map((code: any, i: number) => (
                      <div key={`icd-${i}`} className="flex items-baseline gap-2 px-3 py-1.5 bg-blue-50/60 rounded-lg">
                        <span className="text-xs font-mono font-bold text-blue-700 flex-shrink-0">{code.code}</span>
                        <span className="text-sm text-gray-700 truncate">{code.description}</span>
                      </div>
                    ))}
                  </div>
                )}

                {cptCodes.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">CPT Procedures</span>
                    {cptCodes.map((code: any, i: number) => (
                      <div key={`cpt-${i}`} className="flex items-baseline gap-2 px-3 py-1.5 bg-purple-50/60 rounded-lg">
                        <span className="text-xs font-mono font-bold text-purple-700 flex-shrink-0">{code.code}</span>
                        <span className="text-sm text-gray-700 truncate">{code.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 bg-gray-50/80 border-t border-gray-100">
          <div className="flex items-center justify-end gap-3 w-full">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={confirming}
              className="rounded-xl h-10 px-5"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={confirming}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl h-10 px-6 flex items-center gap-2 shadow-md shadow-purple-200"
            >
              {confirming ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={15} />
                  {noteStatus === 'signed' ? 'Send to EHR' : 'Finish & Send to EHR'}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
